import { useRef } from 'react';
import { Navigation, Volume2, VolumeX, XCircle, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';

/**
 * VoiceNavPanel — Display-only panel for navigation status & step list.
 * Voice announcements are handled by CrisisZonePage's voice engine (always active).
 */
export const VoiceNavPanel = ({ destination, steps, summary, voiceOn, setVoiceOn, onClose, hillWarnings = [] }) => {
  const currentStepRef = useRef(0);

  const handleClose = () => {
    window.speechSynthesis?.cancel();
    onClose();
  };

  if (!destination) return null;

  const hasSteepWarnings = hillWarnings.some(h => h.severity === 'Steep' || h.severity === 'Very Steep');
  const nextStep = steps?.[currentStepRef.current] || steps?.[0];
  const nextStepDist = nextStep?.distance;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-2" data-testid="voice-nav-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-semibold text-gray-800 truncate">{destination.name}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => { setVoiceOn(!voiceOn); if (voiceOn) window.speechSynthesis?.cancel(); }} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors" data-testid="voice-toggle">
            {voiceOn ? <Volume2 className="w-4 h-4 text-purple-500" /> : <VolumeX className="w-4 h-4 text-gray-400" />}
          </button>
          <button onClick={handleClose} className="p-2 rounded-md hover:bg-gray-100 transition-colors" data-testid="close-nav">
            <XCircle className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {nextStep && (
        <div className="bg-purple-50 rounded-lg px-3 py-2 text-sm font-medium text-gray-800" data-testid="next-step">
          {nextStep.text}
          {nextStepDist > 0 && <span className="text-purple-400 ml-1 text-xs">({nextStepDist > 1000 ? `${(nextStepDist / 1000).toFixed(1)}km` : `${Math.round(nextStepDist)}m`})</span>}
        </div>
      )}

      {summary && (
        <div className="flex gap-3 text-xs text-gray-500">
          <span>{(summary.totalDistance / 1000).toFixed(1)} km</span>
          <span>{Math.round(summary.totalTime / 60)} min</span>
          {hillWarnings.length > 0 && (
            <span className={`flex items-center gap-0.5 ${hasSteepWarnings ? 'text-red-400' : 'text-amber-400'}`}>
              <AlertTriangle className="w-3 h-3" />
              {hillWarnings.length} hill{hillWarnings.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {hillWarnings.length > 0 && (
        <div className="flex gap-1 overflow-x-auto pb-0.5" data-testid="hill-warning-strip">
          {hillWarnings.slice(0, 5).map((hw, i) => (
            <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium flex-shrink-0"
              style={{
                backgroundColor: hw.severity === 'Steep' || hw.severity === 'Very Steep' ? '#ef444420' : '#f9731620',
                color: hw.severity === 'Steep' || hw.severity === 'Very Steep' ? '#ef4444' : '#f97316',
              }}>
              {hw.isUphill ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
              {Math.abs(hw.gradient)}%
            </div>
          ))}
        </div>
      )}

      {steps?.length > 0 && (
        <div className="max-h-24 overflow-y-auto space-y-1 scrollbar-thin">
          {steps.map((s, i) => (
            <div key={i} className={`flex items-start gap-2 text-xs ${i === currentStepRef.current ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
              <span className="text-purple-400 font-bold mt-px">{i + 1}</span>
              <span>{s.text} {s.distance > 0 && <span className="text-gray-400">({s.distance > 1000 ? `${(s.distance / 1000).toFixed(1)}km` : `${Math.round(s.distance)}m`})</span>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
